import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Typography,
} from '@growdash/design-system/atoms';
import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import {
  PaginatedResponsePopularMenuItem,
  PopularMenuItem,
} from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
// @ts-ignore
import { useRef } from 'react';

type Props = {
  data: PaginatedResponsePopularMenuItem | undefined;
  isLoading: boolean;
  className?: string;
};

export default function PopularItems({
  data,
  isLoading,
  className = '',
}: Props) {
  const currency = useCurrency();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  const ItemCard = ({
    item,
    currency,
  }: {
    item: PopularMenuItem;
    currency: string;
  }) => (
    <div className="min-w-52 p-2 border border-tertiary  rounded-lg flex items-start gap-2">
      <GrowintelImage
        className="w-16 min-w-16 !h-16 border border-border/40 rounded-lg object-cover"
        src={item.item_image ?? ''}
      />
      <div className="minw-h-16 pt-1">
        <Typography className=" font-medium text-[0.8125rem] !leading-[1.2] line-clamp-2">
          {item.name}
        </Typography>
        <div>
          <Typography className="font-bold text-xs mt-1">
            {item.average_price.toFixed(2)} {currency}
          </Typography>
          <Typography className="font-medium text-xs text-tertiary !leading-[1em] mt-0.5">
            {item.brand_name}
          </Typography>
        </div>
      </div>
    </div>
  );

  const hasItems = data?.items && data.items.length > 0;

  return (
    <Card className={`${className} animate-fade-in min-h-48 shadow-none`}>
      <CardHeader className="border-b-[1px] border-b-border py-4 flex flex-row justify-between items-center">
        <CardTitle className="inline">Popular Items</CardTitle>
        {!isLoading && hasItems && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={scrollLeft}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Scroll Left</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={scrollRight}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Scroll Right</span>
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div>
          <div
            ref={scrollContainerRef}
            className="flex items-center gap-2 overflow-x-auto scrollbar-hidden scroll-smooth"
          >
            {!isLoading ? (
              data?.items
                ?.slice(0, 5)
                .map((item) => (
                  <ItemCard item={item} currency={currency ?? ''} />
                ))
            ) : (
              <>
                <div className="w-full col-span-12 py-12 flex justify-center items-center animate-pulse">
                  <Loader2 className="animate-spin ease-in-out" />
                </div>
              </>
            )}
          </div>
        </div>

        <Typography className="mt-4" variant="caption">
          Top 5 of last month
        </Typography>
      </CardContent>
    </Card>
  );
}
